// components/ChatMain.tsx
import React from 'react'
import {
  ChatMessages,
  ChatInput,
  ChatHeader,
  useChatPageAPI,
} from '@components'
import { Fade, Slide, Box, CircularProgress } from '@mui/material'
import { useChatStore } from '@client/store'

interface ChatMainProps {
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>
  chat_uuid: string
}

export function ChatMain({ setIsGenerating, chat_uuid }: ChatMainProps) {
  const { messages, user, character } = useChatStore()
  const { handleRemoveMessage, handleEditMessage, onGenerateImage } =
    useChatPageAPI(messages, user, character, chat_uuid, setIsGenerating)

  const hasValidData = character && user

  if (!hasValidData) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <CircularProgress />
        <Box sx={{ mt: 2, color: 'text.secondary' }}>
          Loading chat data...
        </Box>
      </Box>
    )
  }

  // Use messages directly from store - first message is already included
  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          width: '100%',
          color: 'white',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            pt: 2,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 960 }}>
            <ChatHeader />
          </Box>
        </Box>

        <Box
          role="region"
          aria-label="Chat messages"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            scrollBehavior: 'smooth',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 960 }}>
            <ChatMessages
              previous_message={messages}  // Direct from store
              setIsGenerating={setIsGenerating}
              onRemove={handleRemoveMessage}
              onEditClick={handleEditMessage}
              onGenerateImage={onGenerateImage}
            />
          </Box>
        </Box>

        <Slide direction="up" in timeout={400}>
          <Box
            sx={{
              flexShrink: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 960 }}>
              <ChatInput chat_uuid={chat_uuid} />
            </Box>
          </Box>
        </Slide>
      </Box>
    </Fade>
  )
}